import {sendEvent} from '@github-ui/hydro-analytics'
import {useQueries} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {CommandIconButton, ScopedCommands} from '@github-ui/ui-commands'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {PaperAirplaneIcon, PaperclipIcon, RocketIcon, SquareFillIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {ReactNode, RefObject} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {copilotChatTextAreaId} from '../utils/constants'
import {referencesAreEqual} from '../utils/copilot-chat-helpers'
import type {
  CopilotChatReference,
  DiscussionReference,
  FigmaReference,
  IssueReference,
  PullRequestReference,
  RepositoryReference,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {CopilotImageAttacher} from '../utils/copilot-image-attacher'
import {sendVisionErrorEvent} from '../utils/copilot-image-helpers'
import {CLIPBOARD_MIME_TYPE, CopilotTextAttacher} from '../utils/copilot-text-attacher'
import {useChatAutocomplete} from '../utils/CopilotChatAutocompleteContext'
import {useChatState, useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useChatInput, useTextareaPreview} from '../utils/use-chat-input'
import {AttachmentMenu} from './AttachmentMenu'
import type {AttachmentMenuPanelType} from './AttachmentMenuPanelType'
import {Autocomplete} from './Autocomplete'
import styles from './ChatInput.module.css'
import {ChatInputReferences} from './ChatInputReferences'

const SUPPORTED_LINK_REFERENCE = ['issue', 'pull-request', 'discussion', 'figma']

interface ChatInputProps {
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  onSubmit?: (text: string) => Promise<void>
  onAbort?: () => void
  isStreaming?: boolean
  size?: 'small' | 'large'
  placeholder?: string
  onClickReference?: (event: React.MouseEvent<HTMLAnchorElement>, reference: CopilotChatReference) => void
  hideAttachmentButton?: boolean
  getReferenceVersion?: (reference: CopilotChatReference) => number | undefined
  /** Set of previously generated/referenced file names in this thread, to use as a reference for generating new file names. */
  existingFileNames?: Set<string>
}

type ItemRefType = 'issue' | 'pull-request' | 'discussion' | 'figma'

interface BaseUserInputLink {
  originalText: string
  processedText: string
  type: ItemRefType | 'repo'
  loadedRefData?: boolean
}

interface BaseRepoItemReference extends BaseUserInputLink {
  linkNumber?: number
  repository: {
    name: string
    owner: string
  }
}

interface RepositoryInputLink extends BaseRepoItemReference {
  type: 'repo'
}

interface IssueUserInputLink extends BaseRepoItemReference {
  type: 'issue'
}

interface PrUserInputLink extends BaseRepoItemReference {
  type: 'pull-request'
}

interface DiscussionInputLink extends BaseRepoItemReference {
  type: 'discussion'
}

interface FigmaInputLink extends BaseUserInputLink {
  type: 'figma'
  id?: string
}

type UserInputLink = IssueUserInputLink | RepositoryInputLink | FigmaInputLink | PrUserInputLink | DiscussionInputLink

const getTypeParam = (itemRefType?: ItemRefType): string => {
  switch (itemRefType) {
    case 'issue':
      return 'issue'
    case 'pull-request':
      return 'pull_request'
    case 'discussion':
      return 'discussion'
    default:
      // Return issue as default if we can't figure it out
      return 'issue'
  }
}

export const ChatInput = (props: ChatInputProps) => {
  const state = useChatState()
  const autocomplete = useChatAutocomplete()
  const manager = useChatManager()
  const maxMessagesReached = manager.maxMessagesReached()
  const slashCommandLoading = state.slashCommandLoading.state === 'loading'
  const hideAttachmentButton = props.hideAttachmentButton || false

  const {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll,
    handleChange,
    handleStop,
    handleFocus,
  } = useChatInput({
    isLoading:
      state.isWaitingOnCopilot ||
      slashCommandLoading ||
      state.currentReferences.some(x => x.type === 'image' && !x.attachment.isLoaded),
    onSubmit: props.onSubmit,
    onAbort: props.onAbort,
    textAreaRef: props.textAreaRef,
  })

  const stopResponse = () => {
    sendEvent('dotcom_chat.activate', {target: 'CHAT_MESSAGE_STOP', mode: state.mode})
    void handleStop()
  }

  const sendMessage = () => {
    setUserInputLinks([])
    void handleSubmit()
  }

  const sendMessageToNewThread = () => void handleSubmitToNewThread()

  const editLastMessage = () => {
    const id = state.messages.findLast(m => m.role === 'user')?.id
    if (id) manager.startEditingMessage(id)
  }

  const placeholderText = state.isWaitingOnCopilot
    ? `Copilot is responding…`
    : state.defaultRecipient
      ? undefined
      : maxMessagesReached
        ? 'Message limit reached. To continue chatting with Copilot, start a new conversation.'
        : props.placeholder
          ? props.placeholder
          : 'Ask Copilot'

  if (state.defaultRecipient && text === '' && !typingHasStarted) {
    setText(`@${state.defaultRecipient} `)
  }

  const [userInputLinks, setUserInputLinks] = useState<UserInputLink[]>([])
  const filteredUserInputLinks = useMemo(() => userInputLinks.filter(link => !link.loadedRefData), [userInputLinks])
  const combinedQueries = useQueries({
    queries: filteredUserInputLinks.map(link => ({
      enabled: copilotFeatureFlags.copilotUIRefs,
      queryKey: ['copilot', 'chat-links', 'item-url', link.originalText],
      queryFn: async () => {
        let response
        if (!copilotFeatureFlags.chatAutocomplete) {
          response = await verifiedFetchJSON(`/copilot/chat-links?item_url=${link.originalText}`)
        } else {
          if (link.type === 'figma') {
            response = await verifiedFetchJSON(`/copilot/chat/validate-url?url=${link.originalText}`)
          } else if (link.type === 'repo') {
            response = await verifiedFetchJSON(
              `/copilot/chat/reference/${(link as BaseRepoItemReference).repository.owner}/${
                (link as BaseRepoItemReference).repository.name
              }`,
            )
          } else {
            response = await verifiedFetchJSON(
              `/copilot/chat/reference/${(link as BaseRepoItemReference).repository.owner}/${
                (link as BaseRepoItemReference).repository.name
              }/${getTypeParam(link.type)}/${(link as BaseRepoItemReference).linkNumber}`,
            )
          }
        }
        const queryResponse = await response.json()
        if (
          isRepositoryReference(queryResponse) ||
          isIssueReference(queryResponse) ||
          isPullRequestReference(queryResponse) ||
          isDiscussionReference(queryResponse) ||
          isFigmaReference(queryResponse)
        )
          return queryResponse
      },
    })),
    combine: results => {
      // When a request fails, the `data` is empty and the request is filtered out
      return {
        data: results.map(result => result.data).filter(data => !!data),
        pending: results.some(result => result.isPending),
      }
    },
  })

  // Process completed network requests
  useEffect(() => {
    if (!combinedQueries.pending && combinedQueries.data.length > 0) {
      let newUserInputLinks = [...userInputLinks]
      for (const data of combinedQueries.data) {
        // If the data is not already in the references, add it
        if (
          ['issue', 'pull-request', 'discussion'].includes(data.type) ||
          (copilotFeatureFlags.chatAutocomplete && data.type === 'repository')
        ) {
          newUserInputLinks = newUserInputLinks.map(prevLink => {
            if (referenceAndInputLinkAreEqual(prevLink, data)) {
              return {
                ...prevLink,
                loadedRefData: true,
              }
            }
            return prevLink
          })

          if (!state.currentReferences.find(ref => referencesAreEqual(data, ref))) {
            manager.addReference(data, 'chatInput')
          }
        }

        if (
          state.mode === 'immersive' &&
          copilotFeatureFlags.immersiveFigmaIntegration &&
          data.type === 'figma' &&
          !state.currentReferences.find(ref => referencesAreEqual(data, ref))
        ) {
          newUserInputLinks = newUserInputLinks.map(prevLink => {
            if (referenceAndInputLinkAreEqual(prevLink, data)) {
              return {
                ...prevLink,
                id: data.id,
                loadedRefData: true,
              }
            }
            return prevLink
          })

          if (!state.currentReferences.find(ref => referencesAreEqual(data, ref))) {
            manager.addReference(data, 'chatInput')
          }
        }
      }

      if (copilotFeatureFlags.copilotUIRefs) {
        const {processedText: processedUserInput} = processUserInput(
          text,
          newUserInputLinks,
          state.mode === 'immersive',
          true,
        )

        setText(processedUserInput)
        setUserInputLinks(newUserInputLinks)
      }
    }
  }, [
    combinedQueries.data,
    combinedQueries.pending,
    manager,
    setText,
    state.currentReferences,
    state.mode,
    text,
    userInputLinks,
  ])

  const prevReferences = useRef(state.currentReferences)
  useEffect(() => {
    if (copilotFeatureFlags.copilotUIRefs && state.currentReferences.length < prevReferences.current.length) {
      // Get the index of any non-matching references that have been removed
      const nonMatchingReferenceIndices = userInputLinks
        .map((userInputLink, index) => {
          const match = state.currentReferences.find(currentRef =>
            referenceAndInputLinkAreEqual(userInputLink, currentRef),
          )
          return match ? -1 : index
        })
        .filter(index => index !== -1)

      // Remove the nonMatchingReferenceIndices from the userInputLinks
      const updatedUserInputLinks = userInputLinks.filter((_, index) => !nonMatchingReferenceIndices.includes(index))
      setUserInputLinks(updatedUserInputLinks)
    }

    prevReferences.current = state.currentReferences
  }, [state.currentReferences, userInputLinks])

  const attachmentButtonRef = useRef<HTMLButtonElement>(null)

  const [attachmentMenuPanel, setAttachmentMenuPanel] = useState<AttachmentMenuPanelType | null>(null)
  const addAttachment = () => {
    sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU', mode: state.mode})
    setAttachmentMenuPanel(attachmentMenuPanel === 'attachment-types' ? null : 'attachment-types')
  }

  const setDeepCodeSearch = () => {
    sendEvent('dotcom_chat.activate', {target: 'DEEP_CODESEARCH', mode: state.mode})
    manager.dispatch({type: 'SET_DEEP_CODESEARCH', deepCodeSearch: !state.skillOptions?.deepCodeSearch})
  }

  // For now, we are focused on building out image support in immersive mode only, and only for supported models.
  const {model} = useChatStateValues('model')
  const shouldShowImages =
    (copilotFeatureFlags.attachImagesImmersive && state.mode === 'immersive' && !!model.capabilities.supports.vision) ||
    false

  const handleFileAttachment = useCallback(
    (files: Iterable<File>, uploadType: 'drag' | 'paste') => {
      const imageAttacher = new CopilotImageAttacher(state, manager, autocomplete)
      const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)

      const disallowedFileTypes = []
      for (const file of files) {
        if (shouldShowImages && CopilotImageAttacher.isTypeAllowed(file.type, model))
          void imageAttacher.addImageAttachment(file, uploadType)
        else if (copilotFeatureFlags.pasteTextFiles && CopilotTextAttacher.isAttachable(file))
          void textAttacher.addAttachment(file)
        else disallowedFileTypes.push(file.type)
      }

      if (shouldShowImages && disallowedFileTypes.length > 0) {
        sendVisionErrorEvent('included_unsupported_file', {
          fileTypes: disallowedFileTypes.join(','),
          uploadType,
        })
      }
    },
    [shouldShowImages, model, state, manager, autocomplete, props.existingFileNames],
  )

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = event => {
    const clipboardData = event.clipboardData
    if (clipboardData?.items) {
      const fileArray: File[] = []

      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i]
        if (item && item.kind === 'file') {
          const file = item.getAsFile()
          if (file) fileArray.push(file)
        }

        if (
          item &&
          item.kind === 'string' &&
          ['text', 'text/plain'].includes(item.type) // "text" for tests, "text/plain" for real clipboard
        ) {
          const pastedText = clipboardData.getData(item.type)

          if (copilotFeatureFlags.pasteTextFiles && pastedText.length > 500) {
            const file = new File([pastedText], '', {type: CLIPBOARD_MIME_TYPE})
            if (CopilotTextAttacher.isAttachable(file)) fileArray.push(file)
          } else if (copilotFeatureFlags.copilotUIRefs) {
            event.preventDefault()
            const composedText = `${text.substring(
              0,
              textAreaRef?.current?.selectionStart ?? 0,
            )}${pastedText}${text.substring(textAreaRef?.current?.selectionEnd ?? 0, text.length)}`
            const {processedText: processedUserInput, processedRefs} = processUserInput(
              composedText,
              userInputLinks,
              state.mode === 'immersive',
            )

            setText(processedUserInput)
            setUserInputLinks(processedRefs)
          }
        }
      }

      if (fileArray.length > 0) {
        event.preventDefault()
        handleFileAttachment(fileArray, 'paste')
      }
    }
  }

  const handleFileDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      const files = event.dataTransfer?.files
      if (files) handleFileAttachment(files, 'drag')
    },
    [handleFileAttachment],
  )

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(e)
    if (copilotFeatureFlags.copilotUIRefs) {
      const {processedText: processedUserInput, processedRefs} = processUserInput(
        (e.target as HTMLTextAreaElement).value,
        userInputLinks,
        state.mode === 'immersive',
        true,
      )

      setText(processedUserInput)
      setUserInputLinks(processedRefs)

      // Remove the reference if the link is removed from the input text
      if (processedRefs.length < prevReferences.current.length) {
        // Find the removed reference(s)
        const removedReference = prevReferences.current.filter(
          ref =>
            SUPPORTED_LINK_REFERENCE.includes(ref.type) &&
            !processedRefs.find(x => referenceAndInputLinkAreEqual(x, ref)),
        )
        // Delete reference from state.currentReferences
        for (const reference of removedReference) {
          manager.removeReference(reference)
        }
      }
    }
  }

  const wholeAreaDragDrop = shouldShowImages && copilotFeatureFlags.wholeAreaDragDrop
  useEffect(() => {
    if (wholeAreaDragDrop) return

    const chatInput = textAreaRef?.current
    if (!chatInput) return

    const handleDragOver = (event: DragEvent) => event.preventDefault()

    chatInput.addEventListener('dragover', handleDragOver)
    chatInput.addEventListener('drop', handleFileDrop)

    return () => {
      chatInput.removeEventListener('dragover', handleDragOver)
      chatInput.removeEventListener('drop', handleFileDrop)
    }
  }, [textAreaRef, wholeAreaDragDrop, handleFileDrop])

  return (
    <>
      <ScopedCommands
        commands={{
          'github:cancel': stopResponse,
          'copilot-chat:send-message': sendMessage,
          'copilot-chat:send-message-new-conversation': sendMessageToNewThread,
          'copilot-chat:add-attachment': addAttachment,
          'copilot-chat:edit-last-message': text.length === 0 ? editLastMessage : undefined,
          'copilot-chat:deep-codesearch': setDeepCodeSearch,
        }}
      >
        <form
          className={clsx(styles.container, {
            [styles.containerSmall]: props.size === 'small',
          })}
        >
          <ChatInputReferences
            className={styles.attachments}
            returnFocusRef={attachmentButtonRef}
            tokenSize={props.size === 'small' ? 'small' : 'medium'}
            isLoading={combinedQueries.pending && userInputLinks.some(link => !link.loadedRefData)}
            onClickReference={props.onClickReference}
            getReferenceVersion={props.getReferenceVersion}
          />

          <ScopedCommands.LimitKeybindingScope
            // The default bindings for these are 'escape' and 'enter', so we need to strictly limit scope to avoid
            // breaking all the menu and button functionalities that rely on those keys
            commandIds={['github:cancel', 'copilot-chat:send-message', 'copilot-chat:edit-last-message']}
            className={styles.inputContainer}
            ref={textAreaPreviewContainerRef}
          >
            <Autocomplete>
              <textarea
                autoFocus
                id={copilotChatTextAreaId}
                className={styles.input}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                aria-multiline="true"
                onPaste={handlePaste}
                ref={textAreaRef}
                onScroll={handleScroll}
                onChange={handleTextAreaChange}
                onFocus={handleFocus}
                placeholder={placeholderText}
                value={text}
                data-react-autofocus
                disabled={maxMessagesReached}
                {...testIdProps('copilot-chat-input-textarea')}
              />
            </Autocomplete>
            <TextareaPreview
              text={text}
              textareaPreviewRef={textareaPreviewRef}
              textInputRefs={userInputLinks}
              size={props.size}
            />
          </ScopedCommands.LimitKeybindingScope>

          <div className={styles.trailingActions}>
            {copilotFeatureFlags.showDeepCodeSearchButton && (
              <CommandIconButton
                commandId="copilot-chat:deep-codesearch"
                icon={RocketIcon}
                variant="invisible"
                tooltipDirection="n"
                className={clsx({
                  [styles.clicked]: state.skillOptions?.deepCodeSearch,
                })}
              />
            )}
            {!hideAttachmentButton && (
              <CommandIconButton
                commandId="copilot-chat:add-attachment"
                icon={PaperclipIcon}
                variant="invisible"
                ref={attachmentButtonRef}
                tooltipDirection="n"
              />
            )}

            {props.isStreaming || state.isWaitingOnCopilot ? (
              <StopButton />
            ) : (
              <SubmitButton
                isLoading={
                  slashCommandLoading || state.currentReferences.some(x => x.type === 'image' && !x.attachment.isLoaded)
                }
              />
            )}
          </div>
        </form>
      </ScopedCommands>
      <AttachmentMenu
        inputRef={textAreaRef}
        inputOnChange={handleChange}
        anchorRef={attachmentButtonRef}
        panel={attachmentMenuPanel}
        onPanelChange={setAttachmentMenuPanel}
      />
    </>
  )
}

type TextAreaPreviewProps = {
  text: string
  textInputRefs: UserInputLink[]
  textareaPreviewRef: RefObject<HTMLDivElement>
  size?: 'small' | 'large'
}

function TextareaPreview(props: TextAreaPreviewProps) {
  const {text, textareaPreviewRef, textInputRefs} = props
  const {mention, textAfterMention} = useTextareaPreview(props)

  const processedPreviewText = processPreviewText(text, textInputRefs)

  return (
    <div
      id="copilot-chat-textarea-preview"
      {...testIdProps('copilot-chat-input-textarea-preview')}
      aria-hidden
      className={styles.inputPreview}
      ref={textareaPreviewRef}
      role="presentation"
    >
      {mention ? (
        <>
          <span className={styles.token}>{mention}</span>
          {textAfterMention}
        </>
      ) : (
        <>{processedPreviewText}</>
      )}
    </div>
  )
}

function SubmitButton({isLoading}: {isLoading?: boolean}) {
  return (
    <CommandIconButton
      commandId="copilot-chat:send-message"
      variant="invisible"
      size="medium"
      icon={PaperAirplaneIcon}
      aria-label="Send now"
      disabled={isLoading}
      tooltipDirection="n"
    />
  )
}

function StopButton() {
  return (
    <CommandIconButton
      {...testIdProps('copilot-chat-stop-button')}
      commandId="github:cancel"
      variant="invisible"
      size="medium"
      icon={SquareFillIcon}
      tooltipDirection="n"
      sx={{bg: 'danger.subtle', color: 'danger.fg', ':hover': {bg: 'danger.muted'}}}
    />
  )
}

/**
 * Search the textarea input for supported github-related links
 */
function processUserInput(
  userInput: string,
  previouslyFoundLinks: UserInputLink[],
  isImmersive: boolean,
  requiresTrailingWhiteSpace: boolean = false,
): {processedText: string; processedRefs: UserInputLink[]} {
  const processedRefs: UserInputLink[] = []

  const githubUrlReferencePattern =
    /^(?:http:\/\/github\.localhost|https:\/\/github\.com)\/[^/]+\/[^/]+\/(issues|pull|discussions)\/\d+$/
  const figmaUrlPattern = /^https:\/\/www\.figma\.com\/.+$/

  // Autocomplete Patterns
  const autocompleteRepositoryPattern = /^@([\w-.]+\/[\w-.]+)$/
  const autocompleteItemPattern = /^@([\w-.]+\/[\w-.]+)\/(\w+)\/(\d+)$/

  // Splits the user input and captures any whitespace character (space, tab or newline)
  const splitUserInput = userInput.split(/(\s+)/)
  const processedText = splitUserInput
    .map((text, index) => {
      // Check for duplicates that have already been found
      const duplicateLink = processedRefs.find(ref => ref.originalText === text || ref.processedText === text)
      if (duplicateLink) {
        return duplicateLink.loadedRefData ? duplicateLink.processedText : duplicateLink.originalText
      }

      // Check if link has already been processed
      const prevLink = previouslyFoundLinks.find(link => link.originalText === text || link.processedText === text)
      if (prevLink) {
        processedRefs.push(prevLink)
        // successful network call
        if (prevLink.loadedRefData) return prevLink.processedText
        // network call is still ongoing, still show url in textarea
        return prevLink.originalText
      }

      if (isImmersive && copilotFeatureFlags.immersiveFigmaIntegration && figmaUrlPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text

        const cleanFigmaUrl = text.replace('https://www.', '')
        const truncatedLength = 30

        processedRefs.push({
          originalText: text,
          processedText: `${
            cleanFigmaUrl.length > truncatedLength ? `${cleanFigmaUrl.substring(0, truncatedLength)}...` : cleanFigmaUrl
          }`,
          type: 'figma',
        })

        return text
      }

      if (githubUrlReferencePattern.test(text)) {
        // While the user is typing the url, only process text if there is a space after the url
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text

        // transform link into @${ownerName}/${repoName}/${primitiveType}/${number} format
        const splitIssueUrl = text.split('/')
        const issueNumber = splitIssueUrl[splitIssueUrl.length - 1]!
        const primitiveType = splitIssueUrl[splitIssueUrl.length - 2]!
        const repoName = splitIssueUrl[splitIssueUrl.length - 3]!
        const ownerName = splitIssueUrl[splitIssueUrl.length - 4]!

        processedRefs.push({
          originalText: text,
          processedText: `@${ownerName}/${repoName}/${primitiveType}/${issueNumber}`,
          type: getTypeFromUrlPrimitive(primitiveType),
          linkNumber: Number(issueNumber),
          repository: {
            name: repoName,
            owner: ownerName,
          },
        })
        return text
      }

      // Autocomplete Parsing
      if (autocompleteRepositoryPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text
        const matches = text.match(autocompleteRepositoryPattern)
        const repo = matches?.[1]

        if (repo) {
          const splitRepo = repo.split('/')
          const ownerName = splitRepo[0]!
          const repoName = splitRepo[1]!
          processedRefs.push({
            originalText: text,
            processedText: text,
            type: 'repo',
            repository: {
              name: repoName,
              owner: ownerName,
            },
          })
        }
        return text
      }
      if (autocompleteItemPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text
        const matches = text.match(autocompleteItemPattern)
        const repo = matches?.[1]
        const type = getTypeFromUrlPrimitive(matches?.[2])
        const issue = matches?.[3]

        if (repo && type && issue) {
          const splitIssueUrl = repo.split('/')
          const ownerName = splitIssueUrl[0]!
          const repoName = splitIssueUrl[1]!

          processedRefs.push({
            originalText: text,
            processedText: text,
            type,
            linkNumber: Number(issue),
            repository: {
              name: repoName,
              owner: ownerName,
            },
          })
        }
        return text
      }

      return text
    })
    .join('')

  return {processedText, processedRefs}
}

/**
 * Add additional UI features to detected GitHub Links/Refs.
 */
function processPreviewText(text: string, textInputRefs: UserInputLink[]): Array<string | ReactNode> {
  const processedPreviewText: Array<string | ReactNode> = []
  const words = text.split(/(\s+)/)

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    let processedWord: string | React.ReactNode = word

    const inputRef = textInputRefs.find(ref => ref.processedText === word)

    if (inputRef?.loadedRefData || inputRef?.type === 'figma') {
      processedWord = (
        <span
          key={`${inputRef.originalText}-${i}`}
          className={styles.inputPreviewRef}
          {...testIdProps('input-preview-ref')}
        >
          {inputRef.processedText}
        </span>
      )
    }

    processedPreviewText.push(processedWord)
  }

  return processedPreviewText
}

/*
 * Convert URL primitive type to our internal type
 */
function getTypeFromUrlPrimitive(primitiveType?: string): UserInputLink['type'] {
  switch (primitiveType) {
    case 'discussions':
      return 'discussion'
    case 'pull':
      return 'pull-request'
    case 'issues':
    default:
      return 'issue'
  }
}

const isIssueReference = (queryResponse: unknown): queryResponse is IssueReference => {
  return (queryResponse as IssueReference).type === 'issue'
}

const isPullRequestReference = (queryResponse: unknown): queryResponse is PullRequestReference => {
  return (queryResponse as PullRequestReference).type === 'pull-request'
}

const isDiscussionReference = (queryResponse: unknown): queryResponse is DiscussionReference => {
  return (queryResponse as DiscussionReference).type === 'discussion'
}

const isFigmaReference = (queryResponse: unknown): queryResponse is FigmaReference => {
  return (queryResponse as FigmaReference).type === 'figma'
}

const isRepositoryReference = (queryResponse: unknown): queryResponse is RepositoryReference => {
  return (queryResponse as RepositoryReference).type === 'repository'
}

const referenceAndInputLinkAreEqual = (inputLink: UserInputLink, reference: CopilotChatReference): boolean => {
  switch (inputLink.type) {
    case 'issue':
    case 'discussion':
    case 'pull-request':
      return inputLink.type === reference.type && inputLink.linkNumber === reference.number
    case 'repo':
      return (
        reference.type === 'repository' &&
        inputLink.repository.owner === reference.ownerLogin &&
        inputLink.repository.name === reference.name
      )
    case 'figma':
      return inputLink.type === reference.type && inputLink.originalText === reference.url
    default:
      return false
  }
}

try{ ChatInput.displayName ||= 'ChatInput' } catch {}
try{ TextareaPreview.displayName ||= 'TextareaPreview' } catch {}
try{ SubmitButton.displayName ||= 'SubmitButton' } catch {}
try{ StopButton.displayName ||= 'StopButton' } catch {}